import type { CapsuleRecommendProduct } from './types';

export interface CapsuleAlgorithmRequestParams {
  group_id: number;
  scene?: 'cart';
  strategy?: number;
  useWaterfall?: boolean;
  waterfall_position?: number;
}

export interface CapsuleAlgorithmResponseData {
  acc_list?: unknown;
  frame_list?: unknown;
  waterfall_list?: unknown;
  waterfall_position?: number | string;
}

interface NormalizeCapsuleAlgorithmPageParams {
  currentPosition: number;
  data?: CapsuleAlgorithmResponseData;
  hasGlassInCart: boolean;
  isWaterfallList?: boolean;
  siteName: string;
}

export interface NormalizedCapsuleAlgorithmPage {
  hasMore: boolean;
  nextPosition: number;
  products: CapsuleRecommendProduct[];
}

export function buildCapsuleAlgorithmRequestParams(
  siteName: string,
  waterfallPosition: number,
  algorithmValue?: string,
  cartRecommendSource?: number,
): CapsuleAlgorithmRequestParams {
  if (algorithmValue === 'cart') {
    return cartRecommendSource === 1
      ? {
          group_id: 6,
          scene: 'cart',
          useWaterfall: true,
          waterfall_position: waterfallPosition,
        }
      : { group_id: 6, scene: 'cart' };
  }

  switch (siteName) {
    case 'us':
      return {
        group_id: 6,
        useWaterfall: true,
        waterfall_position: waterfallPosition,
      };
    case 'de':
    case 'fr':
      return { group_id: 6, strategy: 1 };
    case 'ca':
    case 'uk':
    default:
      return { group_id: 6 };
  }
}

function normalizeProductList(value: unknown): CapsuleRecommendProduct[] {
  if (!Array.isArray(value)) return [];

  return value.filter((item): item is CapsuleRecommendProduct => {
    if (!item || typeof item !== 'object') return false;
    const product = item as { name?: unknown; short_name?: unknown };
    return Boolean(product.name || product.short_name);
  });
}

/** 保持原活动页算法推荐的站点分流、配件列表和 '-1' 降级规则。 */
export function normalizeCapsuleAlgorithmPage({
  currentPosition,
  data,
  hasGlassInCart,
  isWaterfallList,
  siteName,
}: NormalizeCapsuleAlgorithmPageParams): NormalizedCapsuleAlgorithmPage {
  if (!data) {
    return { products: [], nextPosition: currentPosition, hasMore: false };
  }

  const usesWaterfallList = isWaterfallList ?? siteName === 'us';
  const shouldFallbackToProductGroup = data.waterfall_position === '-1';
  let sourceList: unknown;

  if (shouldFallbackToProductGroup) {
    sourceList = hasGlassInCart ? data.acc_list : data.waterfall_list;
  } else if (usesWaterfallList) {
    sourceList = data.waterfall_list;
  } else {
    sourceList = hasGlassInCart ? data.acc_list : data.frame_list;
  }

  const products = normalizeProductList(sourceList);
  const nextPosition =
    typeof data.waterfall_position === 'number'
      ? data.waterfall_position
      : currentPosition + products.length;
  const hasAdvancingCursor =
    typeof data.waterfall_position === 'number' && data.waterfall_position > currentPosition;

  return {
    products,
    nextPosition,
    hasMore:
      usesWaterfallList &&
      !shouldFallbackToProductGroup &&
      products.length > 0 &&
      hasAdvancingCursor,
  };
}
